/**
 * Custom Quiz Dropdown Component
 * Replaces native select elements with custom styled dropdowns
 */

class QuizDropdown {
  // Static array to store all dropdown instances
  static allDropdowns = [];
  
  constructor(selectElement) {
    this.selectElement = selectElement;
    this.isOpen = false;
    this.selectedValue = '';
    this.selectedText = '';
    
    // Register this instance
    QuizDropdown.allDropdowns.push(this);
    
    this.init();
  }

  init() {
    this.createDropdown();
    this.attachEventListeners();
    this.setInitialValue();
  }

  createDropdown() {
    // Create dropdown container
    this.dropdownContainer = document.createElement('div');
    this.dropdownContainer.className = 'quiz-dropdown';
    
    // Create button
    this.dropdownButton = document.createElement('button');
    this.dropdownButton.type = 'button';
    this.dropdownButton.className = 'quiz-dropdown__button';
    this.dropdownButton.setAttribute('aria-haspopup', 'listbox');
    this.dropdownButton.setAttribute('aria-expanded', 'false');
    
    // Create selected value display
    this.selectedDisplay = document.createElement('span');
    this.selectedDisplay.className = 'quiz-dropdown__selected';
    this.selectedDisplay.textContent = 'Select One';
    
    // Create chevron icon
    this.chevronIcon = document.createElement('span');
    this.chevronIcon.className = 'quiz-dropdown__chevron';
    this.chevronIcon.innerHTML = `
      <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    
    this.dropdownButton.appendChild(this.selectedDisplay);
    this.dropdownButton.appendChild(this.chevronIcon);
    
    // Create options list
    this.optionsList = document.createElement('ul');
    this.optionsList.className = 'quiz-dropdown__options';
    this.optionsList.setAttribute('role', 'listbox');
    this.optionsList.setAttribute('aria-hidden', 'true');
    
    // Create options from select element
    this.createOptions();
    
    // Assemble dropdown
    this.dropdownContainer.appendChild(this.dropdownButton);
    this.dropdownContainer.appendChild(this.optionsList);
    
    // Replace select with dropdown
    this.selectElement.parentNode.insertBefore(this.dropdownContainer, this.selectElement);
    this.selectElement.style.display = 'none';
  }

  createOptions() {
    const options = this.selectElement.querySelectorAll('option');
    
    options.forEach((option, index) => {
      if (option.value === '') return; // Skip placeholder option
      
      const listItem = document.createElement('li');
      listItem.className = 'quiz-dropdown__option';
      listItem.setAttribute('role', 'option');
      listItem.setAttribute('data-value', option.value);
      listItem.textContent = option.textContent;
      
      this.optionsList.appendChild(listItem);
    });
  }

  attachEventListeners() {
    // Button click to toggle dropdown
    this.dropdownButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });
    
    // Option click to select
    this.optionsList.addEventListener('click', (e) => {
      const option = e.target.closest('.quiz-dropdown__option');
      if (option) {
        this.selectOption(option);
      }
    });
    
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.dropdownContainer.contains(e.target)) {
        this.close();
      }
    });
    
    // Keyboard navigation
    this.dropdownButton.addEventListener('keydown', (e) => {
      this.handleKeydown(e);
    });
    
    this.optionsList.addEventListener('keydown', (e) => {
      this.handleKeydown(e);
    });
  }

  handleKeydown(e) {
    const options = Array.from(this.optionsList.querySelectorAll('.quiz-dropdown__option'));
    const currentIndex = options.findIndex(option => option.classList.contains('quiz-dropdown__option--selected'));
    
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (this.isOpen && currentIndex >= 0) {
          this.selectOption(options[currentIndex]);
        } else {
          this.toggle();
        }
        break;
      case 'Escape':
        this.close();
        this.dropdownButton.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!this.isOpen) {
          this.open();
        } else {
          const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
          this.highlightOption(options[nextIndex]);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!this.isOpen) {
          this.open();
        } else {
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
          this.highlightOption(options[prevIndex]);
        }
        break;
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    // Close all other open dropdowns first
    QuizDropdown.allDropdowns.forEach(dropdown => {
      if (dropdown !== this && dropdown.isOpen) {
        dropdown.close();
      }
    });
    
    this.isOpen = true;
    this.dropdownContainer.classList.add('quiz-dropdown--open');
    this.dropdownButton.setAttribute('aria-expanded', 'true');
    this.optionsList.setAttribute('aria-hidden', 'false');
    
    // Focus first option if none selected
    const selectedOption = this.optionsList.querySelector('.quiz-dropdown__option--selected');
    if (selectedOption) {
      selectedOption.focus();
    } else {
      const firstOption = this.optionsList.querySelector('.quiz-dropdown__option');
      if (firstOption) {
        firstOption.focus();
      }
    }
  }

  close() {
    this.isOpen = false;
    this.dropdownContainer.classList.remove('quiz-dropdown--open');
    this.dropdownButton.setAttribute('aria-expanded', 'false');
    this.optionsList.setAttribute('aria-hidden', 'true');
    
    // Remove highlight from all options
    this.optionsList.querySelectorAll('.quiz-dropdown__option').forEach(option => {
      option.classList.remove('quiz-dropdown__option--highlighted');
    });
  }

  selectOption(optionElement) {
    const value = optionElement.getAttribute('data-value');
    const text = optionElement.textContent;
    
    this.selectedValue = value;
    this.selectedText = text;
    this.selectedDisplay.textContent = text;
    
    // Update select element
    this.selectElement.value = value;
    this.selectElement.dispatchEvent(new Event('change', { bubbles: true }));
    
    // Update visual state
    this.optionsList.querySelectorAll('.quiz-dropdown__option').forEach(option => {
      option.classList.remove('quiz-dropdown__option--selected');
    });
    optionElement.classList.add('quiz-dropdown__option--selected');
    
    this.close();
    this.dropdownButton.focus();
  }

  highlightOption(optionElement) {
    this.optionsList.querySelectorAll('.quiz-dropdown__option').forEach(option => {
      option.classList.remove('quiz-dropdown__option--highlighted');
    });
    optionElement.classList.add('quiz-dropdown__option--highlighted');
    optionElement.focus();
  }

  setInitialValue() {
    const selectedOption = this.selectElement.querySelector('option:checked');
    if (selectedOption && selectedOption.value) {
      this.selectedValue = selectedOption.value;
      this.selectedText = selectedOption.textContent;
      this.selectedDisplay.textContent = this.selectedText;
      
      // Mark as selected in dropdown
      const dropdownOption = this.optionsList.querySelector(`[data-value="${selectedOption.value}"]`);
      if (dropdownOption) {
        dropdownOption.classList.add('quiz-dropdown__option--selected');
      }
    }
  }

  getValue() {
    return this.selectedValue;
  }

  setValue(value) {
    const option = this.optionsList.querySelector(`[data-value="${value}"]`);
    if (option) {
      this.selectOption(option);
    }
  }
}

// Initialize all quiz dropdowns
function initQuizDropdowns() {
  const selectElements = document.querySelectorAll('.quiz-select');
  
  selectElements.forEach(select => {
    new QuizDropdown(select);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuizDropdowns);
} else {
  initQuizDropdowns();
}

// Export for use in other scripts
window.QuizDropdown = QuizDropdown;
window.initQuizDropdowns = initQuizDropdowns;
